/**
 * CsvImportModal (FEAT-DEEPLINK-001) — upload → preview (create/update/error) →
 * confirm → result. Upsert matches on the android/ios link pair; failed rows can
 * be downloaded as a CSV. Validation + classification happen server-side.
 */
import React, { useState, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Flex, Text, Badge, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, HStack, Icon, useToast, Link,
} from '@chakra-ui/react';
import { FiUploadCloud, FiDownload, FiFileText } from 'react-icons/fi';
import deepLinksApi, { downloadBlob } from '../../api/deepLinks';

const actionBadge = { create: 'green', update: 'blue', error: 'red' };

function toCsv(rows) {
  const headers = ['name', 'description', 'category', 'android_link', 'ios_link', 'web_fallback_url', 'status', 'error'];
  const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
  const lines = [headers.join(',')];
  rows.forEach((r) => lines.push([r.name, '', r.category, r.android_link, r.ios_link, r.web_fallback_url, r.status, r.reason].map(esc).join(',')));
  return '﻿' + lines.join('\n');
}

export default function CsvImportModal({ isOpen, onClose, onImported }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [step, setStep] = useState('select'); // select | preview | result
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { rows, summary }
  const [result, setResult] = useState(null);

  const reset = () => { setStep('select'); setFile(null); setPreview(null); setResult(null); setBusy(false); };
  const close = () => { reset(); onClose(); };

  const onFile = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true);
    try {
      const res = await deepLinksApi.importPreview(f);
      setPreview(res.data.data); setStep('preview');
    } catch (e) {
      toast({ title: 'Preview failed', description: e?.response?.data?.error || e.message, status: 'error' });
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const res = await deepLinksApi.importApply(file);
      setResult(res.data.data); setStep('result');
      onImported && onImported();
    } catch (e) {
      toast({ title: 'Import failed', description: e?.response?.data?.error || e.message, status: 'error' });
    } finally { setBusy(false); }
  };

  const downloadSample = async () => {
    try { const res = await deepLinksApi.sampleCsv(); downloadBlob(res.data, 'deep-links-sample.csv'); }
    catch (e) { toast({ title: 'Could not download sample', status: 'error' }); }
  };
  const downloadErrors = (rows) => downloadBlob(new Blob([toCsv(rows)], { type: 'text/csv' }), 'deep-links-failed-rows.csv');

  const s = preview?.summary;

  return (
    <Modal isOpen={isOpen} onClose={close} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="17px" fontWeight={700}>Import deep links from CSV</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          {step === 'select' && (
            <Box>
              <Box
                borderWidth="2px" borderStyle="dashed" borderColor="#d6dae0" borderRadius="14px"
                p={8} textAlign="center" cursor="pointer" _hover={{ borderColor: 'brand.400', bg: 'brand.50' }}
                onClick={() => fileRef.current?.click()}
              >
                <Icon as={FiUploadCloud} boxSize={8} color="gray.400" mb={2} />
                <Text fontWeight={600}>Click to choose a CSV file</Text>
                <Text fontSize="xs" color="gray.500" mt={1}>Header row required · columns in any order · up to 5,000 rows</Text>
                <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => onFile(e.target.files?.[0])} />
              </Box>
              <HStack mt={3} justify="space-between">
                <Button leftIcon={<FiDownload />} size="sm" variant="ghost" onClick={downloadSample}>Download sample CSV</Button>
                <Text fontSize="xs" color="gray.500">Rows are matched to existing links by the Android + iOS pair (upsert).</Text>
              </HStack>
            </Box>
          )}

          {step === 'preview' && preview && (
            <Box>
              <HStack mb={3} spacing={3}>
                <HStack><FiFileText /><Text fontSize="sm" noOfLines={1}>{file?.name}</Text></HStack>
                <Badge colorScheme="green">{s.create} create</Badge>
                <Badge colorScheme="blue">{s.update} update</Badge>
                <Badge colorScheme="red">{s.error} error</Badge>
                <Text fontSize="xs" color="gray.500">of {s.total} rows</Text>
              </HStack>
              <TableContainer maxH="46vh" overflowY="auto" borderWidth="1px" borderRadius="10px">
                <Table size="sm" variant="simple">
                  <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                    <Tr><Th>#</Th><Th>Action</Th><Th>Name</Th><Th>Android</Th><Th>iOS</Th><Th>Note</Th></Tr>
                  </Thead>
                  <Tbody>
                    {preview.rows.map((r) => (
                      <Tr key={r.index} bg={r.action === 'error' ? 'red.50' : undefined}>
                        <Td color="gray.400">{r.index + 1}</Td>
                        <Td><Badge colorScheme={actionBadge[r.action]}>{r.action}</Badge></Td>
                        <Td maxW="160px" isTruncated>{r.name}</Td>
                        <Td maxW="150px" isTruncated fontFamily="mono" fontSize="11px">{r.android_link}</Td>
                        <Td maxW="150px" isTruncated fontFamily="mono" fontSize="11px">{r.ios_link}</Td>
                        <Td color="red.500" fontSize="12px">{r.reason}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
              {s.error > 0 && (
                <Button mt={2} size="xs" variant="link" colorScheme="red" leftIcon={<FiDownload />}
                  onClick={() => downloadErrors(preview.rows.filter((r) => r.action === 'error'))}>
                  Download failed rows
                </Button>
              )}
            </Box>
          )}

          {step === 'result' && result && (
            <Box textAlign="center" py={6}>
              <Text fontSize="lg" fontWeight={700} mb={2}>Import complete</Text>
              <HStack justify="center" spacing={3}>
                <Badge colorScheme="green" fontSize="13px" px={2} py={1}>{result.created} created</Badge>
                <Badge colorScheme="blue" fontSize="13px" px={2} py={1}>{result.updated} updated</Badge>
                <Badge colorScheme="red" fontSize="13px" px={2} py={1}>{result.errors?.length || 0} skipped</Badge>
              </HStack>
              {result.errors?.length > 0 && (
                <Button mt={4} size="sm" variant="outline" colorScheme="red" leftIcon={<FiDownload />}
                  onClick={() => downloadErrors(result.errors)}>
                  Download {result.errors.length} failed row(s)
                </Button>
              )}
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px">
          {step === 'select' && <Button variant="ghost" onClick={close}>Cancel</Button>}
          {step === 'preview' && (
            <>
              <Button variant="ghost" onClick={reset} isDisabled={busy}>Choose another file</Button>
              <Button colorScheme="brand" onClick={apply} isLoading={busy} isDisabled={!s || (s.create + s.update) === 0}>
                Import {s ? s.create + s.update : 0} row(s)
              </Button>
            </>
          )}
          {step === 'result' && <Button colorScheme="brand" onClick={close}>Done</Button>}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
